import { T } from 'src/shared/helpers';
import {
    AttributePreviewAction,
    AttributePreviewItems,
    AttributePreviewOption,
    ComposerAttributeNode,
    ComposerAttributeNodeLink,
    ComposerAttributeOptionNode,
    ComposerAttributePreviewType,
    ComposerAttributeType,
    ComposerComparisonOperator,
    ComposerLogicalOperator,
    ComposerOptionActionNode,
    ComposerOptionActionsType,
    ComposerTemplateNode,
    ComposerTemplateSideRestrictions,
    GetActionTypeFromAiType,
    GetActionTypeFromValue,
    GetActionValueFromType
} from '../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { ComposerAreaNode } from './../../../configurator-components/composer-editors/attributes/composerAttributesModels';
import { getParentGuid, getParentIDNumeric, getParentType, randomCode, tryParsePlatformValue } from './composerHelpers';

export class ComposerAttributeNodeAdapter {
    static saveAttributes(attributes: ComposerAttributeNode[]): {
        attributes: MPlaza.Attributes;
        groups: MPlaza.AttributeGroups;
        steps: MPlaza.AttributeSteps;
    } {
        const resultAttributes = new MPlaza.Attributes();
        const resultGroups = new MPlaza.AttributeGroups();
        const resultSteps = new MPlaza.AttributeSteps();

        attributes.forEach((attribute) => {
            if (attribute.type === ComposerAttributeType.Attribute) {
                resultAttributes.push(ComposerAttributeNodeAdapter.fromNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeGroup) {
                resultGroups.push(ComposerAttributeNodeAdapter.fromGroupNode(attribute));
            } else if (attribute.type === ComposerAttributeType.AttributeStep) {
                resultSteps.push(ComposerAttributeNodeAdapter.fromStepNode(attribute));
            }
        });
        return { attributes: resultAttributes, groups: resultGroups, steps: resultSteps };
    }

    static fromJSON(json: any): MPlaza.Attribute {
        const attribute = ComposerAttributeNodeAdapter.createNew();
        if (!json) {
            return attribute;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            attribute.set('attributeGuid', parsedJSON.attributeGuid || MPlaza.generateUUID());
            attribute.set('attributeCode', parsedJSON.attributeCode || randomCode('attribute'));
            attribute.set('stepGuid', parsedJSON.stepGuid || Zakeke.getEmptyUUID());
            attribute.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
            attribute.set('stepID', parsedJSON.stepID || -1);
            attribute.set('groupID', parsedJSON.groupID || -1);
            attribute.set('name', parsedJSON.name || '');
            attribute.set('description', parsedJSON.description || '');
            attribute.set('displayOrder', parsedJSON.displayOrder || 0);
            attribute.set('defaultValue', parsedJSON.defaultValue || '');
            attribute.set('options', parsedJSON.options || []);
            attribute.set('preActions', parsedJSON.preActions || []);
            attribute.set('valueActions', parsedJSON.valueActions || []);
            attribute.set('postActions', parsedJSON.postActions || []);
            attribute.set('pricings', parsedJSON.pricings || []);
            attribute.set('constraintExpression', parsedJSON.constraintExpression || null);
            attribute.set('meshSelector', parsedJSON.meshSelector || null);
            attribute.set('sceneCameraLocationID', parsedJSON.sceneCameraLocationID || null);
            attribute.set('hideOptionsLabel', !!parsedJSON.hideOptionsLabel);
        }
        return attribute;
    }

    static fromJSONOption(json: any): MPlaza.AttributeOption {
        const option = ComposerAttributeNodeAdapter.createNewOption();
        if (!json) {
            return option;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            option.set('optionGuid', parsedJSON.optionGuid || MPlaza.generateUUID());
            option.set('name', parsedJSON.name || '');
            option.set('displayOrder', parsedJSON.displayOrder || 0);
            option.set('optionCode', parsedJSON.optionCode || randomCode('option'));
            option.set('imageUrl', parsedJSON.imageUrl || '');
            option.set('constraintExpression', parsedJSON.constraintExpression || null);
            option.set('isDefault', !!parsedJSON.isDefault);
            option.set('actions', parsedJSON.actions || []);
        }
        return option;
    }

    static fromJSONAction(json: any): MPlaza.AttributeSceneAction {
        const action = ComposerAttributeNodeAdapter.createNewAction();
        if (!json) {
            return action;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            action.set('actionGuid', parsedJSON.actionGuid || MPlaza.generateUUID());
            action.set('name', parsedJSON.name || '');
            action.set('selector', parsedJSON.selector || '');
            action.set('value', parsedJSON.value || '');
        }
        return action;
    }

    static fromJSONStep(json: any): MPlaza.AttributeStep {
        const step = ComposerAttributeNodeAdapter.createNewStep();
        if (!json) {
            return step;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            step.set('stepGuid', parsedJSON.stepGuid || MPlaza.generateUUID());
            step.set('name', parsedJSON.name || '');
            step.set('displayOrder', parsedJSON.displayOrder || 0);
            step.set('groupGuid', parsedJSON.groupGuid || Zakeke.getEmptyUUID());
        }
        return step;
    }

    static fromJSONGroup(json: any): MPlaza.AttributeGroup {
        const group = ComposerAttributeNodeAdapter.createNewGroup();
        if (!json) {
            return group;
        }
        const parsedJSON = typeof json === 'string' ? JSON.parse(json) : json;
        if (parsedJSON) {
            group.set('groupGuid', parsedJSON.groupGuid || MPlaza.generateUUID());
            group.set('name', parsedJSON.name || '');
            group.set('displayOrder', parsedJSON.displayOrder || 0);
        }
        return group;
    }

    static createExpression(link: ComposerAttributeNodeLink): MPlaza.AttributeProposition {
        const expression = new MPlaza.AttributeProposition();
        expression.set('attributeGuid', link.attributeID || '');
        expression.set('op', link.operation as unknown as MPlaza.ComparisonOperator);
        expression.set('value', link.optionID || '');
        return expression;
    }

    static createConstraint(links: ComposerAttributeNodeLink[], operation: ComposerLogicalOperator): MPlaza.AttributeExpression {
        const expression = new MPlaza.AttributeExpression();
        const expressions = links.map((link) => {
            return ComposerAttributeNodeAdapter.createExpression(link);
        });
        expression.set('expressions', expressions);
        expression.set('op', operation as unknown as MPlaza.LogicalOperator);
        return expression;
    }

    static fromNode(node: ComposerAttributeNode | null): MPlaza.Attribute {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNew();
        }
        console.log('FROM NODE', node);
        const attribute = new MPlaza.Attribute();
        attribute.set('attributeGuid', node.id || MPlaza.generateUUID());
        attribute.set('attributeID', node.idNumeric || -1);
        attribute.set('attributeCode', node.code || randomCode('attribute'));
        attribute.set('name', node.name || '');
        attribute.set('description', node.description || '');
        attribute.set('displayOrder', node.displayOrder || 0);
        attribute.set('stepGuid', node.parentType === ComposerAttributeType.AttributeStep ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('stepID', node.parentType === ComposerAttributeType.AttributeStep ? node.parentIDNumeric : -1);
        attribute.set('groupGuid', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentID : Zakeke.getEmptyUUID());
        attribute.set('groupID', node.parentType === ComposerAttributeType.AttributeGroup ? node.parentIDNumeric : -1);
        const options = new MPlaza.AttributeOptions();
        node.options.forEach((option) => {
            options.push(ComposerAttributeNodeAdapter.fromOptionNode(option));
        });
        attribute.set('options', options);
        attribute.set('optionPreviewShapeID', (node.previewType as unknown as MPlaza.AttributeOptionPreviewShape) || 1);
        attribute.set('constraintExpression', ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND));
        attribute.set('meshSelector', node.objects || null);
        attribute.set('sceneCameraLocationID', node.camera || null);
        attribute.set('hideOptionsLabel', node.hideOptionsLabel || false);
        return attribute;
    }

    static fromGroupNode(node: ComposerAttributeNode | null): MPlaza.AttributeGroup {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewGroup();
        }
        const group = new MPlaza.AttributeGroup();
        group.set('groupGuid', node.id || MPlaza.generateUUID());
        group.set('groupID', node.idNumeric || -1);
        group.set('name', node.name || '');
        group.set('displayOrder', node.displayOrder || 0);
        group.set('imageUrl', node.img || '');
        group.set('sceneCameraLocationID', node.camera || null);
        group.set('meshSelector', node.objects || null);
        return group;
    }

    static fromStepNode(node: ComposerAttributeNode | null): MPlaza.AttributeStep {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewStep();
        }
        const step = new MPlaza.AttributeStep();
        step.set('stepGuid', node.id || MPlaza.generateUUID());
        step.set('stepID', node.idNumeric || -1);
        step.set('name', node.name || '');
        step.set('displayOrder', node.displayOrder || 0);
        step.set('groupGuid', node.parentID || Zakeke.getEmptyUUID());
        return step;
    }

    static fromOptionNode(node: ComposerAttributeOptionNode | null): MPlaza.AttributeOption {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewOption();
        }
        const option = new MPlaza.AttributeOption();
        option.set('optionGuid', node.id || MPlaza.generateUUID());
        option.set('optionID', node.idNumeric || -1);
        option.set('name', node.name || '');
        option.set('displayOrder', node.displayOrder || 0);
        option.set('optionCode', node.code || randomCode('option'));
        option.set('imageUrl', node.img || '');
        option.set('constraintExpression', ComposerAttributeNodeAdapter.createConstraint(node.links, node.linkOperation ?? ComposerLogicalOperator.AND));
        option.set('isDefault', node.isDefault || false);
        const actions = new MPlaza.AttributeSceneActions();
        node.actions.forEach((action) => {
            actions.push(ComposerAttributeNodeAdapter.fromActionNode(action));
        });
        option.set('actions', actions);
        return option;
    }

    static fromActionNode(node: ComposerOptionActionNode | null): MPlaza.AttributeSceneAction {
        if (!node) {
            return ComposerAttributeNodeAdapter.createNewAction();
        }
        const action = new MPlaza.AttributeSceneAction();
        action.set('actionGuid', node.id || MPlaza.generateUUID());
        action.set('name', node.name || '');
        action.set('actionTypeID', GetActionValueFromType(node?.type || ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', node.sceneElementType || MPlaza.SceneElementType.Mesh);
        action.set('execOrder', node.displayOrder || 0);
        action.set('selector', node.selector || '');
        action.set('propertyName', node.property || '');
        action.set('value', node.value || '');
        console.log('📤 fromActionNode - Converting to backend:', {
            nodeType: node.type,
            nodeTypeName: ComposerOptionActionsType[node.type],
            actionTypeID: GetActionValueFromType(node?.type || ComposerOptionActionsType.SHOW_OBJECTS),
            selector: node.selector,
            property: node.property,
            value: node.value,
            sceneElementTypeID: node.sceneElementType || MPlaza.SceneElementType.Mesh,
            execOrder: node.displayOrder || 0
        });
        return action;
    }

    static createNew(displayOrder?: number): MPlaza.Attribute {
        const attribute = new MPlaza.Attribute();
        attribute.set('attributeID', -1);
        attribute.set('attributeGuid', MPlaza.generateUUID());
        attribute.set('attributeCode', randomCode('attribute'));
        // attribute.set('attributeTypeID', MPlaza.AttributeType.Options);
        attribute.set('stepGuid', Zakeke.getEmptyUUID());
        attribute.set('groupGuid', Zakeke.getEmptyUUID());
        attribute.set('stepID', -1);
        attribute.set('groupID', -1);
        // attribute.set('constraintExpression', null);
        attribute.set('name', '');
        attribute.set('description', '');
        attribute.set('displayOrder', displayOrder || 0);

        return attribute;
    }
    static createNewGroup(displayOrder?: number): MPlaza.AttributeGroup {
        const group = new MPlaza.AttributeGroup();
        group.set('groupGuid', MPlaza.generateUUID());
        group.set('groupID', -1);
        group.set('name', '');
        group.set('displayOrder', displayOrder || 0);
        return group;
    }

    static createNewStep(displayOrder?: number): MPlaza.AttributeStep {
        const step = new MPlaza.AttributeStep();
        step.set('stepGuid', MPlaza.generateUUID());
        step.set('stepID', -1);
        step.set('name', '');
        step.set('displayOrder', displayOrder || 0);
        return step;
    }

    static createNewOption(displayOrder?: number): MPlaza.AttributeOption {
        const option = new MPlaza.AttributeOption();
        option.set('optionGuid', MPlaza.generateUUID());
        option.set('optionID', -1);
        option.set('name', '');
        option.set('optionCode', randomCode('option'));
        option.set('displayOrder', displayOrder || 0);
        return option;
    }

    static createNewAction(): MPlaza.AttributeSceneAction {
        const action = new MPlaza.AttributeSceneAction();
        action.set('actionGuid', MPlaza.generateUUID());
        action.set('name', '');
        action.set('execOrder', 0);
        action.set('actionTypeID', GetActionValueFromType(ComposerOptionActionsType.SHOW_OBJECTS));
        action.set('sceneElementTypeID', MPlaza.SceneElementType.Mesh);
        action.set('selector', '');
        return action;
    }

    static toNode(attribute: MPlaza.Attribute): ComposerAttributeNode {
        if (!attribute) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        const attributeCode = attribute.get('attributeCode') || randomCode('attribute');
        return {
            id: attribute.get('attributeGuid'),
            idNumeric: attribute.get('attributeID') || -1,
            code: attributeCode,
            description: attribute.get('description') || '',
            name: attribute.get('name') || T._('Attribute', 'Admin'),
            displayOrder: attribute.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            objects: attribute.get('meshSelector') || '',
            camera: attribute.get('sceneCameraLocationID') || '',
            parentID: getParentGuid(attribute.get('stepGuid'), attribute.get('groupGuid')),
            parentIDNumeric: getParentIDNumeric(attribute.get('stepID'), attribute.get('groupID')),
            parentType: getParentType(attribute.get('stepGuid'), attribute.get('groupGuid')),
            options: attribute.get('options')?.map((option) => ComposerAttributeNodeAdapter.toNodeOption(attribute.get('attributeGuid'), option)) || [],
            platformAttribute: attributeCode.startsWith('{') ? tryParsePlatformValue(attributeCode) : undefined,
            previewType: (attribute.get('optionPreviewShapeID') as unknown as ComposerAttributePreviewType) || ComposerAttributePreviewType.Rectangle,
            hideOptionsLabel: attribute.get('hideOptionsLabel') || false,
            linkOperation: (attribute.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                attribute
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            brokenValues: new Map(),
            data: attribute.toJSON()
        };
    }

    static toNodeActionFromAiAttribute(attributeID: string, optionID: string, action: AttributePreviewAction): ComposerOptionActionNode {
        const nodeAction = ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        nodeAction.type = GetActionTypeFromAiType(action.type);
        nodeAction.selector = action.objects.join(';');
        nodeAction.value = action.material;
        return nodeAction;
    }

    static toNodeOptionFromAiAttribute(attributeID: string, option: AttributePreviewOption): ComposerAttributeOptionNode {
        const nodeOption = ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        nodeOption.name = option.name;
        nodeOption.actions = option.actions.map((action) => ComposerAttributeNodeAdapter.toNodeActionFromAiAttribute(attributeID, nodeOption.id, action));
        return nodeOption;
    }

    static toNodeFromAiAttribute(attribute: AttributePreviewItems): ComposerAttributeNode {
        const nodeAttribute = ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        nodeAttribute.name = attribute.name;
        nodeAttribute.options = attribute.options.map((option) => ComposerAttributeNodeAdapter.toNodeOptionFromAiAttribute(nodeAttribute.id, option));
        return nodeAttribute;
    }

    static toNodeOption(attributeID: string, option: MPlaza.AttributeOption): ComposerAttributeOptionNode {
        if (!option) {
            return ComposerAttributeNodeAdapter.toNodeOption(attributeID, ComposerAttributeNodeAdapter.createNewOption());
        }
        return {
            id: option.get('optionGuid'),
            idNumeric: option.get('optionID') || -1,
            code: option.get('optionCode') || randomCode('option'),
            name: option.get('name') || T._('Option', 'Admin'),
            displayOrder: option.get('displayOrder') || 0,
            type: ComposerAttributeType.Attribute,
            img: option.get('imageUrl') || '',
            attributeID: attributeID,
            isDefault: option.get('isDefault') || false,
            platformAttribute: tryParsePlatformValue(option.get('optionCode')),
            linkOperation: (option.get('constraintExpression')?.get('op') as unknown as ComposerLogicalOperator) || ComposerLogicalOperator.AND,
            links:
                option
                    .get('constraintExpression')
                    ?.get('propositions')
                    ?.map((link: any) => ({
                        attributeID: link.attributeGuid || '',
                        optionID: link.value || '',
                        operation: link.op || ComposerComparisonOperator.EQUAL
                    })) || [],
            actions: option.get('actions')?.map((action) => ComposerAttributeNodeAdapter.toNodeAction(attributeID, option.get('optionGuid'), action)) || [],
            brokenValues: new Map(),
            data: option.toJSON()
        } as ComposerAttributeOptionNode;
    }

    static toNodeAction(attributeID: string, optionID: string, action: MPlaza.AttributeSceneAction): ComposerOptionActionNode {
        if (!action) {
            return ComposerAttributeNodeAdapter.toNodeAction(attributeID, optionID, ComposerAttributeNodeAdapter.createNewAction());
        }
        return {
            id: action.get('actionGuid'),
            name: action.get('name') || T._('Action', 'Admin'),
            type: GetActionTypeFromValue(action.get('actionTypeID'), action.get('value') ?? ''),
            sceneElementType: action.get('sceneElementTypeID') || MPlaza.SceneElementType.Mesh,
            displayOrder: action.get('execOrder') || 0,
            optionID: optionID,
            attributeID: attributeID,
            selector: action.get('selector') || '',
            property: action.get('propertyName') || '',
            value: action.get('value') || '',
            brokenValues: new Map(),
            data: action.toJSON()
        } as ComposerOptionActionNode;
    }

    static toStepNode(step: MPlaza.AttributeStep): ComposerAttributeNode {
        if (!step) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        return {
            id: step.get('stepGuid'),
            idNumeric: step.get('stepID') || -1,
            code: step.get('stepGuid') || '',
            description: '',
            name: step.get('name') || T._('Step', 'Admin'),
            displayOrder: step.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeStep,
            parentID: step.get('groupGuid') || undefined,
            parentIDNumeric: step.get('groupID') || -1,
            options: [],
            links: [],
            brokenValues: new Map(),
            data: step.toJSON()
        };
    }

    static toGroupNode(group: MPlaza.AttributeGroup): ComposerAttributeNode {
        if (!group) {
            return ComposerAttributeNodeAdapter.toNode(ComposerAttributeNodeAdapter.createNew());
        }
        return {
            id: group.get('groupGuid'),
            idNumeric: group.get('groupID') || -1,
            code: group.get('groupGuid') || '',
            name: group.get('name') || T._('Group', 'Admin'),
            description: '',
            displayOrder: group.get('displayOrder') || 0,
            type: ComposerAttributeType.AttributeGroup,
            img: group.get('imageUrl') || '',
            parentID: undefined,
            parentIDNumeric: -1,
            camera: group.get('sceneCameraLocationID') || undefined,
            objects: group.get('meshSelector') || undefined,
            options: [],
            links: [],
            brokenValues: new Map(),
            data: group.toJSON()
        };
    }

    static adjustValue(attribute: ComposerAttributeNode): ComposerAttributeNode {
        const adjustedAttribute = { ...attribute };
        if (adjustedAttribute.options && adjustedAttribute.options.every((o) => !o.isDefault)) {
            console.log('ALL DEFAULT', adjustedAttribute.options);
            adjustedAttribute.options = adjustedAttribute.options.map((option, index) => {
                return {
                    ...option,
                    isDefault: index === 0 // Set the first option as default
                };
            });
        }
        return adjustedAttribute;
    }

    static adjustValues(attributes: ComposerAttributeNode[]): ComposerAttributeNode[] {
        return attributes.map(ComposerAttributeNodeAdapter.adjustValue);
    }
}

export class ComposerTemplateNodeAdapter {
    static toNode(id: number, name: string, design: any, nodeID: string | null = null, model: MPlaza.Model, sides: MPlaza.Sides): ComposerTemplateNode {
        const designObj: MPlaza.Design = design
            ? new MPlaza.Design(JSON.parse(JSON.stringify(design)))
            : ComposerTemplateNodeAdapter.createNewDesign(name, model, sides);
        const designSides = designObj.get('sides');
        const restrictions = new Map();
        for (const restriction of designSides.models) {
            const uploadRestrictions = restriction?.get('uploadRestrictions');
            const objRestriction: ComposerTemplateSideRestrictions = {
                userCanAddTexts: restrictions?.get('canAddText') || false,
                userCanAddImages: restrictions?.get('canAddImage') || false,
                isGalleryEnabled: !restrictions?.get('disableSellerImages'),
                userCanAddUploadImages: uploadRestrictions?.isUserImageAllowed || false,
                jpgUploadEnabled: uploadRestrictions?.isJpgAllowed || false,
                pngUploadEnabled: uploadRestrictions?.isPngAllowed || false,
                svgUploadEnabled: uploadRestrictions?.isSvgAllowed || false,
                pdfUploadEnabled: uploadRestrictions?.isPdfAllowed || false,
                pdfWithRasterizationEnabled: uploadRestrictions?.isPdfWithRasterAllowed || false,
                epsUploadEnabled: uploadRestrictions?.isEpsAllowed || false
            };
            restrictions.set(restriction.get('sideID'), objRestriction);
        }
        return {
            templateID: id,
            designDocID: design?.docID || '',
            name: name,
            design: designObj,

            //restriction
            restrictions: restrictions,
            //ui
            id: nodeID ?? MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static fromTemplateNode(node: ComposerTemplateNode): MPlaza.Design {
        const design = new MPlaza.Design(JSON.parse(JSON.stringify(node.design)));
        const designSides = design.get('sides');
        for (const designSide of designSides.models) {
            const sideRestrictions = node.restrictions.get(designSide.get('sideID'));
            designSide?.set('canAddText', !!sideRestrictions?.userCanAddTexts);
            designSide?.set('canAddImage', !!sideRestrictions?.userCanAddImages);
            designSide?.set('disableSellerImages', !sideRestrictions?.isGalleryEnabled);
            const uploadRestrictions = designSide?.get('uploadRestrictions');

            if (uploadRestrictions) {
                uploadRestrictions.isUserImageAllowed = sideRestrictions?.userCanAddUploadImages || false;
                uploadRestrictions.isJpgAllowed = sideRestrictions?.jpgUploadEnabled || false;
                uploadRestrictions.isPngAllowed = sideRestrictions?.pngUploadEnabled || false;
                uploadRestrictions.isSvgAllowed = sideRestrictions?.svgUploadEnabled || false;
                uploadRestrictions.isPdfAllowed = sideRestrictions?.pdfUploadEnabled || false;
                uploadRestrictions.isPdfWithRasterAllowed = sideRestrictions?.pdfWithRasterizationEnabled || false;
                uploadRestrictions.isEpsAllowed = sideRestrictions?.epsUploadEnabled || false;
            }
        }
        return design;
    }

    static createNewDesign(name: string, model: MPlaza.Model, sides: MPlaza.Sides | MPlaza.Side): MPlaza.Design {
        const design = new MPlaza.Design();
        design.set('name', name);
        design.setModel(model);
        design.setPrintType(model.get('printTypes').at(0));
        design.set('sides', new MPlaza.DesignSides());
        if (sides instanceof MPlaza.Side) {
            const designSide = new (MPlaza.DesignSide as any)({
                modelID: model.get('modelID'),
                colorID: model.get('colors').at(0).get('colorID'),
                sideID: sides.get('sideID'),
                canAddText: true,
                canAddImage: true,
                disableSellerImages: false,
                maxNrTexts: null,
                maxNrImages: null,
                uploadRestrictions: {
                    isSvgAllowed: true,
                    isPngAllowed: true,
                    isJpgAllowed: true
                }
            });
            design.get('sides').add(designSide);
        } else {
            for (const side of sides.models) {
                const designSide = new (MPlaza.DesignSide as any)({
                    modelID: model.get('modelID'),
                    colorID: model.get('colors').at(0).get('colorID'),
                    sideID: side.get('sideID'),
                    canAddText: true,
                    canAddImage: true,
                    disableSellerImages: false,
                    maxNrTexts: null,
                    maxNrImages: null,
                    uploadRestrictions: {
                        isSvgAllowed: true,
                        isPngAllowed: true,
                        isJpgAllowed: true
                    }
                });
                design.get('sides').add(designSide);
            }
        }
        design.tempID = Zakeke.generateUUID();
        return design;
    }

    static createNewTemplate(name: string, model: MPlaza.Model, side: MPlaza.Side): ComposerTemplateNode {
        return {
            templateID: 0,
            designDocID: '',
            name: name,
            design: ComposerTemplateNodeAdapter.createNewDesign(name, model, side),

            //restriction
            restrictions: new Map(),
            //ui
            id: MPlaza.generateUUID(),
            data: null, //items?.toJSON(),
            expanded: true
        };
    }

    static toNodeArea(side: MPlaza.Side, meshSides: MPlaza.ModelSceneMeshSides): ComposerAreaNode {
        const meshSide = meshSides.find((mesh) => mesh.get('sideID') === side.get('sideID'));
        const desktopTextureResolution = meshSide?.get('config')?.get('general')?.get('desktopTextureResolutionWidth') || undefined;
        const mobileTextureResolution = meshSide?.get('config')?.get('general')?.get('mobileTextureResolutionWidth') || undefined;
        return {
            name: side.get('name') || '',
            sideID: side.get('sideID') || 0,
            ppcm: side.get('ppcm') || 1,
            width: side.get('areas').at(0).get('boundsWidth') || 0,
            height: side.get('areas').at(0).get('boundsHeight') || 0,
            //effect: presente commentato nel codice ma forse deprecato?
            camera: (side as any).get('cameraLocationID') || undefined,
            uvMap: meshSide?.get('uvChannel') || 0,
            customTextureResolution: !!desktopTextureResolution || !!mobileTextureResolution,
            desktopTextureResolution: desktopTextureResolution,
            mobileTextureResolution: mobileTextureResolution,
            originalSide: side ?? new MPlaza.Side(),
            objects:
                meshSides
                    ?.filter((mesh) => mesh.get('sideID') === side.get('sideID'))
                    .map((mesh) => mesh.get('meshID'))
                    .join(';') || '',

            //ui
            id: (side as any).get('id') || MPlaza.generateUUID(),
            data: side.toJSON(),
            templateID: ''
        };
    }

    static fromAreaNode(node: ComposerAreaNode | null): { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } {
        const result: { side: MPlaza.Side; modelSides: MPlaza.ModelSceneMeshSides } = {
            side: node?.originalSide || new MPlaza.Side(),
            modelSides: new MPlaza.ModelSceneMeshSides()
        };
        console.log('FROM AREA NODE', node);
        (result.side as any).set('id', node?.id || MPlaza.generateUUID());
        result.side.set('sideID', node?.sideID || 0);
        (result.side as any).set('sideGuid', node?.id || MPlaza.generateUUID());
        result.side.set('name', node?.name || '');
        result.side.set('ppcm', node?.ppcm || 1024 / (node?.width || 1));
        if (result.side.get('areas').length > 0) {
            const area = result.side.get('areas').at(0);
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            area.set('areaID', 0);
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
            areas.add(area);
            result.side.set('areas', areas);
        }
        for (const meshID of node?.objects.split(';') || []) {
            const mesh = new MPlaza.ModelSceneMeshSide();
            mesh.set('meshID', meshID);
            mesh.set('sideID', result.side.get('sideID'));
            (mesh as any)?.set('cameraLocationID', node?.camera);
            mesh.set('uvChannel', node?.uvMap || 0);
            const generalConfig = mesh.getOrCreateConfig().getGeneralConfig();
            generalConfig?.set('desktopTextureResolutionHeight', node?.desktopTextureResolution ?? 1024);
            generalConfig?.set('mobileTextureResolutionHeight', node?.mobileTextureResolution ?? 1024);
            mesh.getConfig()?.set('general', generalConfig);
            result.modelSides.add(mesh);
        }
        return result;
    }

    static fromAreaNodeToSide(node: ComposerAreaNode | null): MPlaza.Side {
        const side = node?.originalSide || new MPlaza.Side();
        (side as any).set('id', node?.id || MPlaza.generateUUID());
        side.set('sideID', node?.sideID || 0);
        side.set('name', node?.name || '');
        side.set('ppcm', node?.ppcm || 1024 / (node?.width || 1));
        if (side.get('areas').length > 0) {
            const area = side.get('areas').at(0);
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
        } else {
            const areas = new MPlaza.Areas();
            const area = new MPlaza.Area();
            area.set('areaID', 0);
            area.set('boundsWidth', node?.width || 0);
            area.set('boundsHeight', node?.height || 0);
            areas.add(area);
            side.set('areas', areas);
        }
        return side;
    }

    static saveTemplates(
        templates: ComposerTemplateNode[],
        areas: ComposerAreaNode[]
    ): {
        designs: MPlaza.Designs;
        sides: MPlaza.Sides;
        modelSceneMeshSides: MPlaza.ModelSceneMeshSides;
    } {
        const resultDesigns = new MPlaza.Designs();
        const resultSides = new MPlaza.Sides();
        const resultModelSceneMeshSides = new MPlaza.ModelSceneMeshSides();
        templates.forEach((template) => {
            resultDesigns.push(ComposerTemplateNodeAdapter.fromTemplateNode(template));
        });
        areas.forEach((area) => {
            const { side, modelSides } = ComposerTemplateNodeAdapter.fromAreaNode(area);
            resultSides.push(side);
            modelSides.forEach((meshSide) => {
                resultModelSceneMeshSides.push(meshSide);
            });
        });
        return { designs: resultDesigns, sides: resultSides, modelSceneMeshSides: resultModelSceneMeshSides };
    }
}
